// CompanyOverviewPage.js
import React from 'react';
import { Box, Typography, Container, Breadcrumbs, Link as MuiLink, List, ListItem, ListItemText } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const CompanyOverviewPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Legal & Company', path: '/company' },
    { name: 'Company Overview', path: '/company' },
  ];

  const sections = [
    {
      title: 'Our Vision',
      description: 'To revolutionize the global scrap trading industry by creating a transparent, efficient, and sustainable marketplace that benefits all stakeholders and contributes to a circular economy.',
    },
    {
      title: 'Our Mission',
      description: 'To connect buyers and sellers of scrap materials worldwide through an innovative digital platform, facilitating seamless transactions, providing market intelligence, and promoting environmentally responsible practices.',
    },
    {
      title: 'Our Values',
      details: [
        'Integrity: Upholding the highest ethical standards in all our operations.',
        'Innovation: Continuously seeking new ways to improve the trading experience and sustainability.',
        'Sustainability: Committed to environmental stewardship and resource conservation.',
        'Customer Focus: Prioritizing the needs and success of our users.',
        'Collaboration: Fostering strong partnerships for collective growth and impact.',
      ],
    },
    {
      title: 'What We Do',
      description: 'We provide a comprehensive online platform for trading various scrap materials, including ferrous, non-ferrous, and specialized scrap. Our services include:',
      details: [
        'Listing and browsing scrap materials.',
        'Secure transaction processing.',
        'Market insights and pricing data.',
        'Logistics and verification support.',
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Company Overview
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          Learn about our company's journey, core principles, and what drives us to build a better future for the recycling industry.
        </Typography>

        <Box sx={{ mt: 6 }}>
          {sections.map((section, index) => (
            <Box
              key={index}
              sx={{
                mb: 6,
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                background: 'background.paper',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'grey.200',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 16px 32px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: 'primary.dark' }}>
                {section.title}
              </Typography>
              {section.description && (
                <Typography variant="body1" sx={{ mb: section.details ? 3 : 0, color: 'text.primary' }}>
                  {section.description}
                </Typography>
              )}
              {section.details && (
                <List dense disablePadding>
                  {section.details.map((detail, detailIndex) => (
                    <ListItem key={detailIndex} disablePadding sx={{ py: 0.5 }}>
                      <ListItemText primary={detail} primaryTypographyProps={{ variant: 'body1', color: 'text.secondary' }} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default CompanyOverviewPage;
